import { ChevronDown, LogIn, Menu, Moon, Search, Sun, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Brand } from "./Brand";
import { ButtonLink } from "./ui";
import { useAuth } from "../features/auth/AuthProvider";
import { roleHome } from "../lib/auth-role";
import {
  isPublicModuleEnabled,
  usePublicModuleVisibility,
} from "../features/access/useModuleVisibility";
import { useI18n } from "../features/i18n/I18nProvider";
import { AccessibilityToolbar } from "../features/accessibility/AccessibilityToolbar";

const homeLink = { to: "/", label: "Inicio", moduleCode: "public.home" };

const governmentLinks = [
  { to: "/intendencia", label: "Intendencia", moduleCode: "public.institution" },
  { to: "/junta-municipal", label: "Junta Municipal", moduleCode: "public.institution" },
  { to: "/dependencias", label: "Dependencias", moduleCode: "public.institution" },
] as const;

const navGroups = [
  {
    label: "Gobierno municipal",
    items: governmentLinks,
  },
  {
    label: "Información pública",
    items: [
      { to: "/transparencia", label: "Transparencia", moduleCode: "public.transparency" },
      { to: "/transparencia?seccion=informacion-publica", label: "Acceso a información pública", moduleCode: "public.transparency" },
      { to: "/transparencia?seccion=leyes-reglamentos", label: "Leyes y reglamentos", moduleCode: "public.transparency" },
      { to: "/obras", label: "Obras públicas", moduleCode: "public.works" },
      { to: "/estado-servicios", label: "Estado de servicios", moduleCode: "public.home" },
    ],
  },
  {
    label: "Trámites y servicios",
    items: [
      { to: "/tramites", label: "Catálogo de trámites", moduleCode: "public.procedures" },
      { to: "/movilidad", label: "Movilidad y servicios", moduleCode: "public.mobility" },
      { to: "/mapa-catastral", label: "Mapa catastral", moduleCode: "public.cadastre" },
    ],
  },
  {
    label: "Piribebuy",
    items: [
      { to: "/turismo-cultura", label: "Turismo y cultura", moduleCode: "public.tourism" },
      { to: "/noticias", label: "Noticias", moduleCode: "public.news" },
    ],
  },
] as const;

function readDarkTheme(): boolean {
  try {
    const savedTheme = window.localStorage.getItem("pby-theme");
    if (savedTheme) return savedTheme === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
}

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [dark, setDark] = useState(readDarkTheme);
  const headerRef = useRef<HTMLElement>(null);
  const institutionStripRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user } = useAuth();
  const { locale, setLocale } = useI18n();
  const { data: visibility = {} } = usePublicModuleVisibility();
  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        isPublicModuleEnabled(visibility, item.moduleCode),
      ),
    }))
    .filter((group) => group.items.length > 0);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try {
      window.localStorage.setItem("pby-theme", dark ? "dark" : "light");
    } catch {
      // El modo visual funciona aunque el navegador impida recordarlo.
    }
  }, [dark]);

  useEffect(() => {
    setActiveMenu(null);
    setMenuOpen(false);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const closeOnOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      const isInsideHeader = headerRef.current?.contains(target) ?? false;
      const isInsideInstitutionStrip =
        institutionStripRef.current?.contains(target) ?? false;
      if (!isInsideHeader && !isInsideInstitutionStrip) {
        setActiveMenu(null);
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveMenu(null);
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <>
      <a className="skip-link" href="#main-content">
        Saltar al contenido
      </a>
      <div className="institution-strip" ref={institutionStripRef}>
          <div className="container institution-strip-inner">
            <span>República del Paraguay · Gobierno Municipal</span>
            <div className="institution-tools">
              <AccessibilityToolbar
                open={activeMenu === "accessibility"}
                onOpenChange={(open) => setActiveMenu(open ? "accessibility" : null)}
              />
              <div
                className="language-selector"
                role="group"
                aria-label="Idioma del portal"
              >
                <button
                  type="button"
                  lang="es-PY"
                  aria-pressed={locale === "es-PY"}
                  className={locale === "es-PY" ? "is-active" : ""}
                  onClick={() => setLocale("es-PY")}
                >
                  Español
                </button>
                <span aria-hidden="true">/</span>
                <button
                  type="button"
                  lang="gn-PY"
                  aria-pressed={locale === "gn-PY"}
                  className={locale === "gn-PY" ? "is-active" : ""}
                  onClick={() => setLocale("gn-PY")}
                >
                  Guaraní Ñe’ẽ
                </button>
              </div>
              <button
                type="button"
                aria-label={dark ? "Activar modo claro" : "Activar modo oscuro"}
                onClick={() => setDark((value) => !value)}
              >
                {dark ? (
                  <Sun aria-hidden="true" size={16} />
                ) : (
                  <Moon aria-hidden="true" size={16} />
                )}
              </button>
            </div>
          </div>
        </div>

      <header className="site-header" ref={headerRef}>
        <div className="container header-main">
          <Brand />
          <div className="header-actions">
            <Link className="header-search" to="/buscar">
              <Search aria-hidden="true" size={18} />
              <span>Buscar</span>
            </Link>
            <ButtonLink to={user ? roleHome(user.role) : "/ingresar"}>
              <LogIn aria-hidden="true" size={18} />
              {user ? "Ir a mi espacio" : "Ingresar"}
            </ButtonLink>
            <button
              className="menu-button"
              type="button"
              aria-expanded={menuOpen}
              aria-controls="main-navigation"
              aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
              onClick={() =>
                setMenuOpen((value) => {
                  if (value) setActiveMenu(null);
                  return !value;
                })
              }
            >
              {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
            </button>
          </div>
        </div>

        <nav
          className={`main-nav ${menuOpen ? "is-open" : ""}`}
          id="main-navigation"
          aria-label="Navegación principal"
        >
          <div className="container main-nav-inner">
            {isPublicModuleEnabled(visibility, homeLink.moduleCode) ? (
              <NavLink
                to={homeLink.to}
                end
                onClick={() => {
                  setMenuOpen(false);
                  setActiveMenu(null);
                }}
              >
                {homeLink.label}
              </NavLink>
            ) : null}
            {visibleGroups.map((group, index) => {
              const isOpen = activeMenu === group.label;
              const submenuId = `main-nav-submenu-${index}`;
              return (
                <div
                  className={`main-nav-group ${isOpen ? "is-open" : ""}`}
                  key={group.label}
                >
                  <button
                    className="main-nav-trigger"
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={submenuId}
                    onClick={() =>
                      setActiveMenu((current) =>
                        current === group.label ? null : group.label,
                      )
                    }
                  >
                    {group.label}
                    <ChevronDown aria-hidden="true" size={16} />
                  </button>
                  <div className="main-nav-submenu" id={submenuId}>
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => {
                          setMenuOpen(false);
                          setActiveMenu(null);
                        }}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
            {isPublicModuleEnabled(visibility, "public.contact") ? (
              <NavLink
                to="/contacto"
                onClick={() => {
                  setMenuOpen(false);
                  setActiveMenu(null);
                }}
              >
                Contacto
              </NavLink>
            ) : null}
            <Link
              className="main-nav-account"
              to={user ? roleHome(user.role) : "/ingresar"}
              onClick={() => {
                setMenuOpen(false);
                setActiveMenu(null);
              }}
            >
              <span className="main-nav-account-icon" aria-hidden="true">
                <LogIn size={20} />
              </span>
              <span className="main-nav-account-copy">
                <strong>{user ? "Ir a mi espacio" : "Ingresar a mi espacio"}</strong>
                <small>
                  {user
                    ? "Continuar al panel personal"
                    : "Trámites, documentos y seguimiento"}
                </small>
              </span>
            </Link>
          </div>
        </nav>
      </header>
    </>
  );
}


